import { FC, memo } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Select, { SelectProps } from '@amzn/awsui-components-core/select';
import { BrandSweetenerOption } from 'src/@types/valueMatching/enums';
import { useTranslate } from 'src/hooks/useTranslate';
import { SectionBaseProps } from '../../types';

const BUYBACK_SECTION_BUNDLE = 'organisms.buybackSection';

export interface BuybackSectionProps extends SectionBaseProps {
    /** Brand sweetener selection (yes/no, default yes) */
    brandSweetenerOption: BrandSweetenerOption;
    /** Callback to update brand sweetener */
    setBrandSweetenerOption: (value: BrandSweetenerOption) => void;
    /** Brand sweetener dropdown options */
    brandSweetenerOptions: SelectProps.Option[];
    /** % of new product price guardrail */
    buybackPercentOfAsinPrice: string;
    /** Callback to update % of new product price guardrail */
    setBuybackPercentOfAsinPrice: (value: string) => void;
    /** % of old product quote guardrail */
    buybackPercentOfBuybackOffer: string;
    /** Callback to update % of old product quote guardrail */
    setBuybackPercentOfBuybackOffer: (value: string) => void;
    /** Absolute value cap INR guardrail */
    buybackAbsoluteValueCap: string;
    /** Callback to update absolute value cap guardrail */
    setBuybackAbsoluteValueCap: (value: string) => void;
    /** Inline error for % of new product price guardrail */
    buybackPercentOfAsinPriceError?: string;
    /** Inline error for % of old product quote guardrail */
    buybackPercentOfBuybackOfferError?: string;
    /** Inline error for absolute value cap guardrail */
    buybackAbsoluteValueCapError?: string;
}

/**
 * Buyback section — renders BUYBACK-specific form fields:
 * brand sweetener and guardrails.
 * Only shown for BUYBACK campaign type (parent controls visibility).
 * Note: "Excluded old product item IDs" moved to AsinInclusionSection
 * to share the "Seller Participation Conditions" container.
 * Owns its own i18n bundle (`organisms.buybackSection`) so translations
 * are local to this component rather than prop-drilled from the page.
 */
const BuybackSection: FC<BuybackSectionProps> = ({
    brandSweetenerOption,
    setBrandSweetenerOption,
    brandSweetenerOptions,
    buybackPercentOfAsinPrice,
    setBuybackPercentOfAsinPrice,
    buybackPercentOfBuybackOffer,
    setBuybackPercentOfBuybackOffer,
    buybackAbsoluteValueCap,
    setBuybackAbsoluteValueCap,
    mode,
    disabled,
    buybackPercentOfAsinPriceError,
    buybackPercentOfBuybackOfferError,
    buybackAbsoluteValueCapError,
}) => {
    const { translate } = useTranslate(BUYBACK_SECTION_BUNDLE);
    const isDisabled = disabled ?? mode === 'view';
    // Mirror CashbackSection's pattern for the FormField error icon a11y label.
    const errorI18n = { errorIconAriaLabel: translate('error_icon_aria_label') };

    return (
        <SpaceBetween size={mode === 'view' ? 's' : 'l'}>
            {/* ── Brand sweetener ───────────────────────────────── */}
            <Container
                header={
                    <Header variant="h2">{translate('section_buyback_brand_sweetener')}</Header>
                }
            >
                <FormField label={translate('brand_sweetener_label')}>
                    <Select
                        selectedOption={
                            brandSweetenerOptions.find((o) => o.value === brandSweetenerOption) ?? null
                        }
                        onChange={({ detail }) =>
                            setBrandSweetenerOption((detail.selectedOption.value ?? BrandSweetenerOption.YES) as BrandSweetenerOption)
                        }
                        options={brandSweetenerOptions}
                        placeholder={translate('brand_sweetener_placeholder')}
                        ariaRequired={true}
                        disabled={isDisabled}
                        data-testid="brand-sweetener"
                    />
                </FormField>
            </Container>

            {/* ── Guardrails (3-column row) ─────────────────────── */}
            <Container
                header={
                    <Header variant="h2">{translate('section_buyback_guardrails')}</Header>
                }
            >
                <ColumnLayout columns={3}>
                    <FormField
                        label={translate('buyback_guardrail_label')}
                        description={translate('buyback_percent_of_asin_price_label')}
                        errorText={buybackPercentOfAsinPriceError}
                        i18nStrings={errorI18n}
                    >
                        <Input
                            value={buybackPercentOfAsinPrice}
                            onChange={({ detail }) => setBuybackPercentOfAsinPrice(detail.value)}
                            type="number"
                            ariaRequired={false}
                            disabled={isDisabled}
                            data-testid="buyback-percent-of-asin-price-guardrail"
                            invalid={!!buybackPercentOfAsinPriceError}
                        />
                    </FormField>
                    <FormField
                        label={translate('buyback_guardrail_label')}
                        description={translate('buyback_percent_of_buyback_offer_label')}
                        errorText={buybackPercentOfBuybackOfferError}
                        i18nStrings={errorI18n}
                    >
                        <Input
                            value={buybackPercentOfBuybackOffer}
                            onChange={({ detail }) => setBuybackPercentOfBuybackOffer(detail.value)}
                            type="number"
                            ariaRequired={false}
                            disabled={isDisabled}
                            data-testid="buyback-percent-of-buyback-offer-guardrail"
                            invalid={!!buybackPercentOfBuybackOfferError}
                        />
                    </FormField>
                    <FormField
                        label={translate('buyback_guardrail_label')}
                        description={translate('buyback_absolute_value_cap_label')}
                        errorText={buybackAbsoluteValueCapError}
                        i18nStrings={errorI18n}
                    >
                        <Input
                            value={buybackAbsoluteValueCap}
                            onChange={({ detail }) => setBuybackAbsoluteValueCap(detail.value)}
                            type="number"
                            ariaRequired={false}
                            disabled={isDisabled}
                            data-testid="buyback-absolute-value-guardrail"
                            invalid={!!buybackAbsoluteValueCapError}
                        />
                    </FormField>
                </ColumnLayout>
            </Container>
        </SpaceBetween>
    );
};

export default memo(BuybackSection);
